import FacetType from '~/constants/FacetType';
import type {
  BoolType,
  Facet,
  FacetData,
  FacetLists,
  FlattenedFacetData,
  ListItem,
  ListType,
  MakeFacet,
  MakesFacet,
  ModelFacet,
  RangeType,
  WaysToSaveFacets
} from '~/contracts';

type TrimCount = Map<string, number>;
type ModelCount = Map<string, { count: number; trims: TrimCount }>;
type MakeCount = Map<string, { count: number; models: ModelCount }>;

function isBoolType(v: Record<string, any>): v is BoolType {
  return v.type === FacetType.Bool;
}

function isFacetData(v: FlattenedFacetData, key: string): v is FacetData {
  return key === 'makes' || key === 'waysToSave';
}

function isListType(v: Record<string, any>): v is ListType<any, ListItem> {
  return (
    v.type === FacetType.AllOfList ||
    v.type === FacetType.AnyOfList ||
    v.type === FacetType.RangeBucket
  );
}

function isMakeType(v: Record<string, any>): v is MakesFacet {
  const firstKey = Object.keys(v).at(0);

  return firstKey ? v[firstKey].models != null : false;
}

function isRangeType(v: Record<string, any>): v is RangeType {
  return v.type === FacetType.Range;
}

function isWaysToSave(
  v: Facet | Partial<WaysToSaveFacets>
): v is Partial<WaysToSaveFacets> {
  const keys: Array<keyof WaysToSaveFacets> = ['isFreeDelivery', 'tagIds'];

  return keys.some(key => Object.hasOwn(v, key));
}

function getBucketCounts(
  facetKey: keyof FacetLists,
  facetData?: ListType<any, ListItem>
): Map<string, number> {
  return new Map(
    (facetData?.buckets ?? []).map((v: ListItem) => [v.key, v.count])
  );
}

function getTrimCounts(model: ModelFacet): TrimCount {
  return new Map(model.trims?.map(v => [v.key, v.count]));
}

function getModelCounts(make: MakeFacet): ModelCount {
  return new Map(
    make.models.map(v => [v.key, { count: v.count, trims: getTrimCounts(v) }])
  );
}

export function getMakeCounts(facetData: Partial<FacetData>): MakeCount {
  return new Map(
    Object.entries(facetData.makes ?? {}).map(([makeKey, make]) => {
      return [
        makeKey,
        {
          count: make.count,
          models: getModelCounts(make)
        }
      ];
    })
  );
}

export default function accumulateFacets(facetData: FlattenedFacetData) {
  return (
    acc: FlattenedFacetData,
    [key, facet]: [string, Facet | Partial<WaysToSaveFacets>]
  ): FlattenedFacetData => {
    if (isBoolType(facet)) {
      (acc[key] as BoolType) = {
        ...facet,
        count: facetData[key]?.count ?? 0
      };

      return acc;
    }

    if (isListType(facet)) {
      const facetKey = key as keyof FacetLists;
      const counts = getBucketCounts(facetKey, facetData[facetKey]);

      (acc[key] as ListType<any, ListItem>) = {
        ...facet,
        buckets: facet.buckets.map<ListItem>(v => ({
          ...v,
          count: counts.get(v.key) ?? 0
        }))
      };

      return acc;
    }

    if (isMakeType(facet)) {
      if (!isFacetData(facetData, key)) {
        return acc;
      }

      const counts = getMakeCounts(facetData);

      (acc as FacetData).makes = Object.fromEntries(
        Object.entries(facet).map(([makeKey, make]) => [
          makeKey,
          {
            ...make,
            count: counts.get(makeKey)?.count ?? 0,
            models: make.models.map(model => ({
              ...model,
              count: counts.get(makeKey)?.models?.get(model.key)?.count ?? 0,
              trims: model.trims.map(trim => ({
                ...trim,
                count:
                  counts
                    .get(makeKey)
                    ?.models?.get(model.key)
                    ?.trims?.get(trim.key) ?? 0
              }))
            }))
          }
        ])
      );

      return acc;
    }

    if (isRangeType(facet)) {
      (acc[key] as RangeType) = {
        ...facet,
        min: facetData[key]?.min ?? facet.min,
        max: facetData[key]?.max ?? facet.max
      };

      return acc;
    }

    if (isWaysToSave(facet)) {
      if (!isFacetData(facetData, key)) {
        return acc;
      }

      acc[key] = Object.entries(facet).reduce(
        accumulateFacets(facetData.waysToSave ?? {}),
        {}
      );

      return acc;
    }

    acc[key] = facet; // Unknown facet type, do nothing to it

    return acc;
  };
}
